import React, { useState } from 'react';
import { Icon, SelectMenu, Button, Typography } from '@scale/scaleui';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import {
  ChatPipelineRoutingDestination,
  ChatPipelineRoutingOverride,
  ChatPipelineRoutingOverrideType,
  ChatPipelineRoutingSource,
  NumericRoutingOverride,
  NumericRoutingTypeOverrideType,
} from '@scale/shared-types';
import { ChatDocumentationLink } from '@scale/shared/chat/documentation';
import { RoutingOverrideTypeSchemaMap } from '@scale/shared/pipelinev3/genai';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface RoutingOverridesBuilderProps {
  name: string;
  existingValue?: Array<ChatPipelineRoutingOverride>;
  updateProperty: (arg: Array<ChatPipelineRoutingOverride>) => void;
  required?: boolean;
  taskTemplateParams?: Record<string, any>;
  typeBuilderMap: TypeBuilderMapType;
}

interface RoutingOverrideOption {
  id: ChatPipelineRoutingOverrideType;
  name: string;
}

const routingOverrideOptions: Array<RoutingOverrideOption> = [
  {
    id: ChatPipelineRoutingOverrideType.StringBasedRoutingOverride,
    name: '[Easy] String Based Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.NumericBasedRoutingOverride,
    name: '[Easy] Numeric Based Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.BelowAgreementThreshold,
    name: '[Easy] Below Agreement Threshold',
  },
  {
    id: ChatPipelineRoutingOverrideType.ProbabilityBasedRoutingOverride,
    name: '[Easy] Probability Based Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.MetricBasedSamplingOverride,
    name: `[Easy] Sampled Reviews Based on Quality Metrics`,
  },
  {
    id: ChatPipelineRoutingOverrideType.WorkerBasedRoutingOverride,
    name: '[Easy] Worker Based Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.RewardModelOverride,
    name: '[Advanced] Reward Model Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.AutoQCRoutingOverride,
    name: '[Advanced] ArchieQC Routing Override',
  },
  {
    id: ChatPipelineRoutingOverrideType.SquadsRoutingOverride,
    name: '[Advanced] Squads Routing Override',
  },
];

const DEFAULT_MODEL_FIELDS: Record<string, any> = {
  modelId: 'gpt_4',
  modelUrl: 'https://api.openai.com/v1/chat/completions',
  modelParams: {
    model: 'gpt-4',
    max_tokens: 1024,
    temperature: 0.5,
    fallbackModel: 'gpt-3.5-turbo',
  },
};

const DEFAULT_MULTIMODAL_MODEL_FIELDS: Record<string, any> = {
  modelId: 'gpt_4o',
  modelUrl: 'https://api.openai.com/v1/chat/completions',
  modelParams: {
    model: 'gpt-4o',
    max_tokens: 1024,
    temperature: 1.0,
  },
};

const getDefaultModelFields = (isMultimodal?: boolean) => {
  return isMultimodal ? DEFAULT_MULTIMODAL_MODEL_FIELDS : DEFAULT_MODEL_FIELDS;
};

const RoutingOverridesBuilder: React.FC<RoutingOverridesBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  taskTemplateParams,
  typeBuilderMap,
}) => {
  const routingOverrides = existingValue;
  const [selectedType, setSelectedType] = useState<ChatPipelineRoutingOverrideType>(
    ChatPipelineRoutingOverrideType.NumericBasedRoutingOverride,
  );
  const [hideHideableOverrides, setHideHideableOverrides] = useState(false);

  // Standardized quality measurement overrides are hideable
  const isHideableOverride = (override: ChatPipelineRoutingOverride) =>
    [
      ChatPipelineRoutingOverrideType.NumericBasedRoutingOverride,
      ChatPipelineRoutingOverrideType.StringBasedRoutingOverride,
    ].includes(override.type) &&
    (override as NumericRoutingOverride)?.thresholds &&
    (override as NumericRoutingOverride).thresholds.some(
      threshold => threshold.stepType === NumericRoutingTypeOverrideType.QualityMeasurement,
    );

  const hideableOverrides = routingOverrides.filter(override => isHideableOverride(override));

  const addRoutingOverride = (
    type: ChatPipelineRoutingOverrideType,
    taskTemplateParams?: Record<string, any>,
  ) => {
    const isMultimodal = taskTemplateParams?.useAttachments; // TODO: make this more robust
    const newRoutingOverrides = [...routingOverrides];
    newRoutingOverrides.push({
      type,
      enabledSources: [ChatPipelineRoutingSource.Attempt],
      destination: ChatPipelineRoutingDestination.L0,
      ...(type === ChatPipelineRoutingOverrideType.RewardModelOverride && {
        ...getDefaultModelFields(isMultimodal),
        transitionReason: 'RM Override',
        confidenceLowerBound: 0.8,
        minPctTurnsConfidentThreshold: 0.5,
        maxAllowedActivations: 3,
        numDaysForExclusiveWorkerToFinish: 3,
        minutesToClaimFor: 300,
      }),
    } as any);
    updateProperty(newRoutingOverrides);
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-lg">{name}</div>}
      <div className="prose text-sm italic mb-4">
        <div>
          Configure automatic and dynamic routing strategies to override the default tasking
          progression. Routing overrides are performed in their configuration order on an OR basis,
          meaning that the first routing override that has its conditions met will be applied.
          Within one routing override (for example, a String Based Routing Override with multiple
          thresholds), each sub-condition must pass for the entire routing override to trigger.
          <div>
            See routing overrides documentation{' '}
            <a
              className="text-primary-500"
              target="_blank"
              rel="noopener noreferrer"
              href={ChatDocumentationLink.RoutingOverrides}
            >
              here.
            </a>
          </div>
        </div>
        <div>
          For exclusive worker routing configs, see{' '}
          <a
            className="text-primary-500"
            target="_blank"
            rel="noopener noreferrer"
            href={ChatDocumentationLink.ExclusiveWorker}
          >
            here.
          </a>
        </div>
      </div>
      {hideableOverrides?.length > 0 && (
        <Button
          className="mb-4"
          variant="secondary"
          onClick={() => setHideHideableOverrides(!hideHideableOverrides)}
        >
          {hideHideableOverrides ? 'Show' : 'Hide'} Standardized Overrides{' '}
          {hideHideableOverrides ? `(${hideableOverrides.length} Hidden)` : ''}
        </Button>
      )}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={routingOverrides}
        setElements={updateProperty}
        getKey={routingOverride => routingOverride?.transitionReason ?? routingOverride.type}
        getTitle={routingOverride => routingOverride.type}
        getSchema={routingOverride => RoutingOverrideTypeSchemaMap[routingOverride.type]}
        getTags={routingOverride =>
          isHideableOverride(routingOverride)
            ? [`(Standardized Override) ${routingOverride.transitionReason ?? ''}`]
            : [routingOverride.transitionReason]
        }
        fullWidth
        hideVisibilityCondition={hideHideableOverrides ? isHideableOverride : undefined}
        taskTemplateParams={taskTemplateParams}
        delimiter={<Typography.Subtitle4>OR</Typography.Subtitle4>}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={routingOverrideOptions}
          selectedOptionId={selectedType}
          setSelected={value => {
            setSelectedType(value as ChatPipelineRoutingOverrideType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addRoutingOverride(selectedType, taskTemplateParams)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default RoutingOverridesBuilder;
